import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { api } from "Services/api";
import { useAppDispatch, useAppSelector } from "State/hooks";
import { useEffect } from "react";
import Topbar from "Pages/Global/Topbar";
import Integrations from "Pages/Integrations";
import HSCallback from "Pages/Integrations/HSCallback";
import Dashboard from "Pages/Dashboard";
import Insights from "Pages/Insights";
import ExecutiveBriefing from "Pages/ExecutiveBriefing";
import AskRevOptica from "Components/AskRevOptica";
import Settings from "Pages/Settings";
import TermsOfService from "Pages/Legal/TermsOfService";
import PrivacyPolicy from "Pages/Legal/PrivacyPolicy";
import Timeline from "Pages/Home_old";
import TimelineV2 from "Pages/TimelineV2";
import DealDetail from "Pages/DealDetail";
import CreateAccount from "Pages/signup/createAccount";
import { ActivateAccount } from "Pages/signup/activateAccount";
import ConnectHubspot from "Pages/signup/connectHubSpot";
import Login from "Pages/SignIn/login";
import SignInPage from "Pages/SignIn";
import ForgotPassword from "Pages/SignIn/ForgotPassword";
import ResetPassword from "Pages/SignIn/ResetPassword";
import ExpiredModal from "Components/ExpiredModal";
import PastDueBanner from "Components/PastDueBanner";
import Subscribe from "Pages/Subscribe";
import SubscribeSuccess from "Pages/Subscribe/Success";
import SubscribeCancel from "Pages/Subscribe/Cancel";

function ScrollToTopLayout() {
  const { pathname } = useLocation();
  useEffect(() => {
    const el = document.querySelector("main.content") as HTMLElement | null;
    if (el) el.scrollTo(0, 0);
  }, [pathname]);
  return <Outlet />;
}

function ProtectedLayout() {
  const dispatch = useAppDispatch();
  const companyId = useAppSelector(s => s.company.companyId);
  useEffect(() => () => { dispatch(api.util.resetApiState()); }, [companyId, dispatch]);
  const hasLocalToken = !!localStorage.getItem("idToken");

  /**
   * Log out every other tab when one of them signs out.
   *
   * The token check above only runs on render, so a tab already sitting on a protected
   * route kept showing authenticated content after another tab logged out — until it
   * happened to navigate or reload. The `storage` event fires in *other* same-origin
   * tabs whenever localStorage changes, which is exactly the missing signal.
   *
   * `localStorage.clear()` reports itself as a single event with `key === null`, so that
   * case has to be handled alongside a direct `idToken` removal. The token is re-read
   * rather than trusted from the event, because a background token refresh also writes
   * `idToken` and must not be mistaken for a sign-out.
   */
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== null && event.key !== "idToken") return;
      if (!localStorage.getItem("idToken")) window.location.replace("/login");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (!hasLocalToken) return <Navigate to="/login" replace />;
  return (
    <>
      <Topbar />
      <PastDueBanner />
      <ExpiredModal />
      <AskRevOptica key={companyId}><Outlet /></AskRevOptica>
    </>
  );
}

function PublicOnlyLayout() {
  const hasLocalToken = !!localStorage.getItem("idToken");
  if (hasLocalToken) {
    const hubspotConnected = localStorage.getItem("hubspotConnected") === "true";
    return <Navigate to={hubspotConnected ? "/home" : "/connect-hubspot"} replace />;
  }
  return <Outlet />;
}

export default function AppRoutes() {
  const hasLocalToken = !!localStorage.getItem("idToken");

  return (
    <Routes>
      <Route element={<ScrollToTopLayout />}>
        {/* Landing — shows SignInPage when not authenticated, Home when authenticated */}
        <Route
          path="/"
          element={hasLocalToken ? <Navigate to="/home" replace /> : <SignInPage />}
        />

        {/* Authenticated routes — Topbar shown */}
        <Route element={<ProtectedLayout />}>
          <Route path="/home" element={<ExecutiveBriefing />} />
          <Route path="/chat" element={<div />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/terms" element={<TermsOfService />} />
          <Route path="/settings/privacy" element={<PrivacyPolicy />} />
          <Route path="/subscribe" element={<Subscribe />} />
          <Route path="/subscribe/success" element={<SubscribeSuccess />} />
          <Route path="/subscribe/cancel" element={<SubscribeCancel />} />
          <Route path="/oauth-callback-hs" element={<HSCallback />} />
          <Route path="/deals/:dealId" element={<DealDetail />} />
        </Route>

        {/* Onboarding — no Topbar, no auth required */}
        <Route path="/signup" element={<CreateAccount />} />
        <Route path="/activate-account" element={<ActivateAccount />} />
        <Route path="/connect-hubspot" element={<ConnectHubspot />} />

        {/* Public auth routes */}
        <Route element={<PublicOnlyLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>

        <Route path="/timeline/:dealId" element={<TimelineV2 />} />
        <Route path="/timelinev2/:dealId" element={<Timeline />} />
        <Route path="/callback" element={<div>Processing login...</div>} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function NotFound() {
  console.debug("NotFound");
  return <h1>Not found</h1>;
}
